import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, USER_TYPE_ROUTES } from '../context/AuthContext';
import { loginDocente, selectTenant } from '../api';
import '../styles/login.css';

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const SchoolIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
);

const ArtemisLogo = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 4L4 28h28L18 4z" fill="white" fillOpacity={0.9} />
    <path d="M18 12l-6 12h12l-6-12z" fill="white" fillOpacity={0.5} />
  </svg>
);

const ROLE_LABEL = {
  teacher: 'Docente',
  utp: 'Jefe UTP',
  admin: 'Administrador',
  master: 'Vista Maestra',
};

export default function LoginView() {
  useEffect(() => { document.title = 'Artemis'; }, []);

  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Paso de selección de establecimiento (correo en varios tenants)
  const [selection, setSelection] = useState(null); // { token, tenants: [...] }
  const [selectingId, setSelectingId] = useState(null);
  const [leaving, setLeaving] = useState(false); // fade-out al elegir una card

  const goToApp = (data) => {
    login(data.access_token, data.user);
    const destination = USER_TYPE_ROUTES[data.user.user_type] ?? '/';
    navigate(destination, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Por favor ingresa tu correo y contraseña.');
      return;
    }

    setLoading(true);
    try {
      const data = await loginDocente(email.trim(), password);
      if (data.requires_selection) {
        setSelection({ token: data.selection_token, tenants: data.tenants });
      } else {
        goToApp(data);
      }
    } catch (err) {
      setError(err.message || 'Credenciales inválidas. Verifica tu correo y contraseña.');
    } finally {
      setLoading(false);
    }
  };

  const handleChooseTenant = async (organizationPersonRoleId) => {
    if (selectingId !== null) return;
    setError('');
    setSelectingId(organizationPersonRoleId);
    setLeaving(true); // arranca el fade-out
    try {
      const [data] = await Promise.all([
        selectTenant(selection.token, organizationPersonRoleId),
        new Promise((resolve) => setTimeout(resolve, 300)), // deja ver el fade-out
      ]);
      goToApp(data);
    } catch (err) {
      setLeaving(false);
      setSelectingId(null);
      setError(err.message || 'No se pudo iniciar sesión en ese establecimiento.');
    }
  };

  const cancelSelection = () => {
    setSelection(null);
    setSelectingId(null);
    setLeaving(false);
    setError('');
    setPassword('');
  };

  if (selection) {
    return (
      <div className="login-page">
        <div
          key="choose"
          className={`login-card login-card-choose${leaving ? ' is-leaving' : ''}`}
        >
          <div className="login-brand">
            <div className="login-brand-logo">
              <ArtemisLogo />
            </div>
            <h1 className="login-brand-title">Artemis</h1>
            <p className="login-brand-subtitle">Sistema de Gestión Educativa</p>
          </div>

          {error && (
            <div className="login-error" role="alert">
              <AlertIcon />
              <span>{error}</span>
            </div>
          )}

          <div className="login-tenant-list">
            {selection.tenants.map((t, i) => (
              <button
                key={t.organization_person_role_id}
                type="button"
                className="login-tenant-card"
                style={{ animationDelay: `${i * 90}ms` }}
                onClick={() => handleChooseTenant(t.organization_person_role_id)}
                disabled={selectingId !== null}
              >
                <span className="login-tenant-icon">
                  {selectingId === t.organization_person_role_id
                    ? <span className="login-spinner login-spinner-dark" />
                    : <SchoolIcon />}
                </span>
                <span className="login-tenant-text">
                  <span className="login-tenant-name">{t.organization_name}</span>
                  <span className="login-tenant-role">
                    {ROLE_LABEL[t.user_type] || t.role_name}
                  </span>
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="login-link-btn"
            onClick={cancelSelection}
            disabled={selectingId !== null}
          >
            Usar otra cuenta
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card" key="form">
        {/* Brand */}
        <div className="login-brand">
          <div className="login-brand-logo">
            <ArtemisLogo />
          </div>
          <h1 className="login-brand-title">Artemis</h1>
          <p className="login-brand-subtitle">Sistema de Gestión Educativa</p>
        </div>

        <h2 className="login-heading">Iniciar sesión</h2>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="login-error" role="alert">
              <AlertIcon />
              <span>{error}</span>
            </div>
          )}

          {/* Email */}
          <div className="login-field">
            <label className="login-label" htmlFor="login-email">
              Correo institucional
            </label>
            <div className="login-input-wrapper">
              <span className="login-input-icon"><MailIcon /></span>
              <input
                id="login-email"
                type="email"
                className={`login-input${error ? ' error' : ''}`}
                placeholder="nombre@colegio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={loading}
              />
            </div>
          </div>

          {/* Password */}
          <div className="login-field">
            <label className="login-label" htmlFor="login-password">
              Contraseña
            </label>
            <div className="login-input-wrapper">
              <span className="login-input-icon"><LockIcon /></span>
              <input
                id="login-password"
                type="password"
                className={`login-input${error ? ' error' : ''}`}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
              />
            </div>
          </div>

          {/* Submit */}
          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? (
              <>
                <span className="login-spinner" />
                Iniciando sesión…
              </>
            ) : (
              'Iniciar sesión'
            )}
          </button>
        </form>

        <p className="login-footer">
          El sistema redirigirá automáticamente según tu rol.
        </p>
      </div>
    </div>
  );
}
